'use client';

// module imports
import { signIn, useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

// file imports
import { LoginCard } from '@/components';

// Detect if we're in an iframe
function isInIframe() {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

export default function LoginPage() {
  const { status, data: session } = useSession();
  const router = useRouter();
  const [popup, setPopup] = useState<Window | null>(null);
  const [popupProvider, setPopupProvider] = useState<string | null>(null);

  // Redirect to appropriate dashboard if already authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      const userType = (session?.user as any)?.userType;
      if (userType === 'admin') {
        router.replace('/admin/dashboard');
      } else {
        router.replace('/dashboard');
      }
    }
  }, [status, session, router]);

  // Poll session while popup is open (for OAuth inside iframe)
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (popup && popupProvider) {
      interval = setInterval(async () => {
        if (popup.closed) {
          setPopup(null);
          setPopupProvider(null);
          clearInterval(interval);
          return;
        }
        const res = await fetch('/api/auth/session');
        const data = await res.json();
        if (data && data.user) {
          popup.close();
          setPopup(null);
          setPopupProvider(null);
          const userType = data.user.userType;
          if (userType === 'admin') {
            router.replace('/admin/dashboard');
          } else {
            router.replace('/dashboard');
          }
          clearInterval(interval);
        }
      }, 1500);
    }
    return () => clearInterval(interval);
  }, [popup, popupProvider, router]);

  // 👇 *** Listen for postMessage from popup (OAuth flow) ***
  useEffect(() => {
    function handleAuthSuccess(event: MessageEvent) {
      if (event.data && event.data.type === 'auth-success') {
        // Get user type from session to determine redirect
        fetch('/api/auth/session')
          .then((res) => res.json())
          .then((data) => {
            const userType = data?.user?.userType;
            if (userType === 'admin') {
              router.replace('/admin/dashboard');
            } else {
              router.replace('/dashboard');
            }
          })
          .catch(() => {
            // Fallback to user dashboard if session fetch fails
            router.replace('/dashboard');
          });
      }
    }
    window.addEventListener('message', handleAuthSuccess);
    return () => window.removeEventListener('message', handleAuthSuccess);
  }, [router]);

  // Handle OAuth popup (Google/LinkedIn)
  function openOAuthPopup(provider: string) {
    if (popup && !popup.closed) {
      popup.focus();
      return;
    }
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    const authUrl = `/api/auth/signin/${provider}?callbackUrl=${encodeURIComponent('/login')}`;
    const popupWindow = window.open(authUrl, `${provider}Popup`, `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars=yes,status=1`);
    if (popupWindow) {
      setPopup(popupWindow);
      setPopupProvider(provider);
      popupWindow.focus();
    } else {
      alert('Popup blocked! Please allow popups for this site.');
    }
  }

  // Fallback for OAuth outside iframe: just use NextAuth's signIn
  function handleOAuth(provider: string) {
    if (isInIframe()) {
      openOAuthPopup(provider);
    } else {
      signIn(provider, { callbackUrl: '/login' });
    }
  }

  if (status === 'loading' || status === 'authenticated') return null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fafbfc] relative">
      <LoginCard onOAuthClick={handleOAuth} />
    </div>
  );
}
